import {
  Alert,
  AppliedFilters,
  Button,
  Container,
  Heading,
  Table,
  Text,
  useModal,
} from "@fluidattacks/design";
import type { Row } from "@tanstack/react-table";
import { Fragment, useCallback, useState } from "react";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useTheme } from "styled-components";

import { EmptyState } from "./empty-state";
import { ExecutionModal } from "./execution";
import { useDevSecOpsFilters } from "./filters";
import { useExecutionsQuery, usePoliciesQuery, useReportQuery } from "./hooks";
import type { IExecution } from "./types";
import { getColumns } from "./utils";

import type {
  ForcesExecutionsInput,
  ForcesExecutionsSortInput,
} from "gql/graphql";
import { useDailyAlert, useSearch } from "hooks";
import { useTable } from "hooks/use-table";
import { useTableServerSideStore } from "utils/tables";
import type { TStore } from "utils/zustand";

const DEFAULT_POLICY = 0;
const PADDING = 1.25;

const GroupDevSecOps: FC = (): JSX.Element => {
  const { t } = useTranslation();
  const { groupName } = useParams() as { groupName: string };
  const theme = useTheme();
  const executionsModalRef = useModal("executions-modal");

  const [search, setSearch] = useSearch();
  const [currentRow, setCurrentRow] = useState<IExecution>();
  const [filters, setFilters] = useState<ForcesExecutionsInput>({});
  const tableHelpers = useTableServerSideStore();
  const { Filters, options, removeFilter } = useDevSecOpsFilters({
    setFilters,
  });

  const openSeeExecutionDetailsModal = useCallback(
    (rowInfo: Row<IExecution>): void => {
      setCurrentRow(rowInfo.original);
      executionsModalRef.open();
    },
    [executionsModalRef],
  );

  const { executions, loading, fetchNextPage } = useExecutionsQuery(
    groupName,
    search,
    filters,
    tableHelpers.sortBy as ForcesExecutionsSortInput[],
  );

  // Table definition
  const { table } = useTable({
    columns: getColumns(),
    data: executions,
    fetchMore: fetchNextPage,
    filters: tableHelpers as unknown as TStore<IExecution, string>,
    id: "dev-sec-ops-table",
    options: {
      actionsOptions: {
        onSearch: setSearch,
        searchPlaceholder: t("organization.devSecOps.searchPlaceholder"),
      },
      filtersMode: "server-side",
      paginationMode: "server-side",
      rowOptions: {
        getId: (row): string => row.executionId,
        onClick: openSeeExecutionDetailsModal,
      },
    },
    total: executions.length,
  });

  const { requestReport } = useReportQuery(groupName);
  const { groupPolicies, policiesLoading } = usePoliciesQuery(groupName);

  const useAlert = useDailyAlert("group-policies", groupName);

  if (
    !loading &&
    !policiesLoading &&
    executions.length === 0 &&
    (search ?? "").length === 0 &&
    Object.values(filters).filter(Boolean).length === 0
  ) {
    return <EmptyState />;
  }

  return (
    <Fragment>
      <Container
        bgColor={theme.palette.gray[50]}
        gap={1.25}
        padding={[1, PADDING, 0, PADDING]}
        px={1.25}
        py={1.25}
      >
        <Heading fontWeight={"bold"} size={"sm"}>
          {t("group.forces.title")}
        </Heading>
        <Text color={theme.palette.gray[800]} pb={0.5} pt={0.5} size={"sm"}>
          {t("group.forces.tableAdvice")}
        </Text>
        <Alert closable={true} mb={1} show={useAlert} variant={"info"}>
          <Text color={theme.palette.info["700"]} size={"md"}>
            <strong>
              {t("group.forces.policiesAlert.temporaryAcceptance.title")}
            </strong>
            {t("group.forces.policiesAlert.temporaryAcceptance.text1")}
            <strong>
              {t("group.forces.policiesAlert.temporaryAcceptance.text2", {
                maxDays:
                  groupPolicies?.group.maxAcceptanceDays ?? DEFAULT_POLICY,
                maxNumber:
                  groupPolicies?.group.maxNumberAcceptances ?? DEFAULT_POLICY,
              })}
            </strong>
            {t("group.forces.policiesAlert.temporaryAcceptance.text3")}
            <strong>
              {t("group.forces.policiesAlert.temporaryAcceptance.text4", {
                maxSeverity:
                  groupPolicies?.group.maxAcceptanceSeverity ?? DEFAULT_POLICY,
                minSeverity:
                  groupPolicies?.group.minAcceptanceSeverity ?? DEFAULT_POLICY,
              })}
            </strong>
          </Text>
          <Text color={theme.palette.info["700"]} size={"md"}>
            <strong>{t("group.forces.policiesAlert.devSecOps.title")}</strong>
            {t("group.forces.policiesAlert.devSecOps.text1")}
            <strong>
              {t("group.forces.policiesAlert.devSecOps.text2", {
                minSeverity:
                  groupPolicies?.group.minBreakingSeverity ?? DEFAULT_POLICY,
              })}
            </strong>
            {t("group.forces.policiesAlert.devSecOps.text3")}
            <strong>
              {t("group.forces.policiesAlert.devSecOps.text4", {
                gracePeriod:
                  groupPolicies?.group.vulnerabilityGracePeriod ??
                  DEFAULT_POLICY,
              })}
            </strong>
          </Text>
        </Alert>
      </Container>
      <Container
        bgColor={theme.palette.gray[50]}
        height={"100hv"}
        padding={[0, 1, 0, 1]}
        px={1.25}
        py={1.25}
      >
        <Table table={table}>
          <Table.Options>
            <Table.Controls>
              <Filters />
              <Container id={"actions-container"}>
                <Button
                  id={"reports"}
                  onClick={requestReport}
                  rightIcon={"arrow-right"}
                >
                  {t("group.findings.buttons.report.text")}
                </Button>
              </Container>
            </Table.Controls>
          </Table.Options>
          <Table.Caption>
            <AppliedFilters onClose={removeFilter} options={options} />
          </Table.Caption>
          <Table.Content />
          <Table.Footer />
        </Table>
        {currentRow ? (
          <ExecutionModal
            execution={currentRow}
            modalRef={executionsModalRef}
          />
        ) : undefined}
      </Container>
    </Fragment>
  );
};

export { GroupDevSecOps };
